import React from 'react';

export interface CardProps {
  variant?: 'default' | 'elevated' | 'interactive';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

const variantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  default: 'bg-gray-900/50 rounded-xl border border-gray-800',
  elevated: 'bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700/50',
  interactive: 'bg-gray-900/50 rounded-xl border border-gray-800 hover:border-gray-700 transition-colors cursor-pointer',
};

const paddingClasses: Record<NonNullable<CardProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export default function Card({
  variant = 'default',
  padding = 'md',
  className = '',
  children,
  onClick,
}: CardProps) {
  const Component = onClick ? 'button' : 'div';
  const interactiveVariant = onClick && variant === 'default' ? 'interactive' : variant;

  return (
    <Component
      className={`
        ${variantClasses[interactiveVariant]}
        ${paddingClasses[padding]}
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </Component>
  );
}

// Subcomponents for structured Card layouts
export function CardHeader({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`flex items-center justify-between mb-4 ${className}`}>
      {children}
    </div>
  );
}

export function CardTitle({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <h3 className={`text-lg font-semibold text-white ${className}`}>
      {children}
    </h3>
  );
}

export function CardContent({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      {children}
    </div>
  );
}
